import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Loader2, ChevronRight, Medal } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const CATEGORIES = [
  "Large Cap", "Mid Cap", "Small Cap", "Multi Cap", "ELSS",
  "Hybrid - Aggressive", "Hybrid - Conservative", "Debt - Liquid"
];

export const QuartileRankingPage = () => {
  const [category, setCategory] = useState("Large Cap");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${category}&limit=30`);
        const filtered = (res.data.funds || [])
          .filter(f => f.planType === "Direct" && f.option === "Growth");
        
        // Assign quartiles (mock data - in real app this would come from API)
        const total = filtered.length;
        const withQuartile = filtered.map((fund, idx) => ({
          ...fund,
          quartile: idx < total * 0.25 ? 1 : idx < total * 0.5 ? 2 : idx < total * 0.75 ? 3 : 4
        }));
        setFunds(withQuartile);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [category]);

  const getQuartileStyle = (quartile) => {
    switch(quartile) {
      case 1: return "bg-green-500/20 text-green-500 border-green-500/50";
      case 2: return "bg-blue-500/20 text-blue-500 border-blue-500/50";
      case 3: return "bg-yellow-500/20 text-yellow-500 border-yellow-500/50";
      case 4: return "bg-red-500/20 text-red-500 border-red-500/50";
      default: return "bg-secondary text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Mutual Fund <span className="gold-text">Quartile Ranking</span>
            </h1>
            <p className="text-muted-foreground">
              See how funds rank within their category
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Quartile Legend */}
            <Card className="bg-card border-border mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 justify-center">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-green-500 text-sm font-bold">Q1</span>
                    <span className="text-sm">Top 25%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center text-blue-500 text-sm font-bold">Q2</span>
                    <span className="text-sm">25-50%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center text-yellow-500 text-sm font-bold">Q3</span>
                    <span className="text-sm">50-75%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-red-500/20 border border-red-500/50 flex items-center justify-center text-red-500 text-sm font-bold">Q4</span>
                    <span className="text-sm">Bottom 25%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    {category} - Quartile Rankings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {funds.map((fund, idx) => (
                      <Link key={fund.schemeCode} to={`/research/${fund.schemeCode}`}>
                        <div className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors border border-border">
                          <div className="flex items-center gap-3">
                            <span className={`w-10 h-10 rounded-full border flex items-center justify-center text-sm font-bold ${getQuartileStyle(fund.quartile)}`}>
                              Q{fund.quartile}
                            </span>
                            <div>
                              <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                              <p className="text-xs text-muted-foreground">{fund.fundHouse}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
